import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Pencil } from 'lucide-react';
import type { AgentCardData, DepartmentRow, DepartmentWithAgents } from '@command-room/shared';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/cn';

interface DeptNode extends DepartmentWithAgents {
  children: DeptNode[];
}

function initials(name: string) {
  return name
    .split(/\s+/)
    .map((s) => s[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();
}

/**
 * Group departments into a tree by parent_dept_id. Departments whose parent is missing
 * (deleted, or filtered out) are treated as roots so nothing disappears from the chart.
 */
function buildTree(departments: DepartmentWithAgents[]): DeptNode[] {
  const nodes = new Map<number, DeptNode>(departments.map((d) => [d.id, { ...d, children: [] }]));
  const roots: DeptNode[] = [];
  for (const node of nodes.values()) {
    const parent = node.parent_dept_id != null ? nodes.get(node.parent_dept_id) : undefined;
    if (parent && parent.id !== node.id) parent.children.push(node);
    else roots.push(node);
  }
  const byName = (a: DeptNode, b: DeptNode) => a.name.localeCompare(b.name);
  roots.sort(byName);
  for (const node of nodes.values()) node.children.sort(byName);
  return roots;
}

/** Head first, then alphabetical. */
function orderMembers(agents: AgentCardData[], headName: string | null): AgentCardData[] {
  return [...agents].sort((a, b) => {
    if (a.name === headName) return -1;
    if (b.name === headName) return 1;
    return a.name.localeCompare(b.name);
  });
}

function AgentNode({ agent, isHead }: { agent: AgentCardData; isHead: boolean }) {
  const inactive = agent.status_badge === 'inactive';
  return (
    <Link
      to={`/agents/${encodeURIComponent(agent.name)}`}
      className={cn(
        'flex w-full items-center gap-2.5 rounded-md border bg-background p-2 text-left shadow-sm transition-colors',
        'hover:border-primary/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        isHead && 'border-primary/30',
      )}
    >
      <Avatar className={cn('h-9 w-9', inactive && 'opacity-60 grayscale')}>
        {agent.agent_icon_url && <AvatarImage src={agent.agent_icon_url} alt={agent.name} />}
        <AvatarFallback className="text-[10px]">{initials(agent.name)}</AvatarFallback>
      </Avatar>
      <div className={cn('min-w-0 flex-1', inactive && 'opacity-60')}>
        <div className="flex items-center gap-1.5">
          <span className="truncate text-sm font-medium">{agent.name}</span>
          {isHead && (
            <Badge variant="secondary" className="shrink-0 px-1.5 py-0 text-[10px]">
              Head
            </Badge>
          )}
        </div>
        {agent.role ? (
          <p className="line-clamp-2 text-xs leading-snug text-muted-foreground">{agent.role}</p>
        ) : (
          <p className="text-xs italic text-muted-foreground/70">No title</p>
        )}
      </div>
    </Link>
  );
}

/**
 * The department's members as an indented list hanging off a rail on the left. Each row
 * draws its own rail segment (the last one stops at its card's middle) plus a tick, so the
 * connectors line up without measuring anything.
 */
function MemberList({ agents, headName }: { agents: AgentCardData[]; headName: string | null }) {
  const ordered = orderMembers(agents, headName);
  return (
    <ul className="ml-3">
      {ordered.map((agent, i) => {
        const isLast = i === ordered.length - 1;
        return (
          <li key={agent.name} className="relative pl-4 pt-2">
            <span
              aria-hidden
              className={cn(
                'absolute left-0 top-0 w-px bg-border',
                isLast ? 'h-[calc(50%+0.25rem)]' : 'h-full',
              )}
            />
            <span
              aria-hidden
              className="absolute left-0 top-[calc(50%+0.25rem)] h-px w-4 bg-border"
            />
            <AgentNode agent={agent} isHead={agent.name === headName} />
          </li>
        );
      })}
    </ul>
  );
}

interface NodeCardProps {
  dept: DepartmentWithAgents;
  /** Every colleague, so a head who sits in another department still resolves. */
  headLookup: Map<string, AgentCardData>;
  onEdit?: (dept: DepartmentRow) => void;
}

function NodeCard({ dept, headLookup, onEdit }: NodeCardProps) {
  const head = dept.head_agent_name ? (headLookup.get(dept.head_agent_name) ?? null) : null;
  // A head who is a member already has a card in the list below; only a head from
  // another department needs the chip.
  const showHeadChip = head !== null && !dept.agents.some((a) => a.name === head.name);

  return (
    <div className="w-60 rounded-lg border bg-card p-3 text-left shadow-sm transition-colors hover:border-primary/40">
      <div className="flex items-start justify-between gap-2">
        <h3 className="min-w-0 truncate text-sm font-semibold">{dept.name}</h3>
        {onEdit && (
          <Button
            variant="ghost"
            size="icon"
            className="-mr-1 -mt-1 h-6 w-6 shrink-0 text-muted-foreground hover:text-foreground"
            onClick={() => onEdit(dept)}
            aria-label={`Edit ${dept.name}`}
          >
            <Pencil className="h-3 w-3" />
          </Button>
        )}
      </div>

      {dept.goal && <p className="mt-1 line-clamp-2 text-xs text-muted-foreground">{dept.goal}</p>}

      {showHeadChip && head && (
        <Link
          to={`/agents/${encodeURIComponent(head.name)}`}
          className="mt-2 inline-flex max-w-full items-center gap-1.5 rounded-full border bg-background px-2 py-0.5 text-xs font-medium transition-colors hover:border-primary/40 hover:text-primary"
        >
          <Avatar className="h-4 w-4">
            {head.agent_icon_url && <AvatarImage src={head.agent_icon_url} alt={head.name} />}
            <AvatarFallback className="text-[8px]">{initials(head.name)}</AvatarFallback>
          </Avatar>
          <span className="truncate">Head: {head.name}</span>
        </Link>
      )}

      <p className="mt-2 text-[10px] uppercase tracking-wider text-muted-foreground">
        {dept.agents.length === 0
          ? 'No members'
          : `${dept.agents.length} ${dept.agents.length === 1 ? 'member' : 'members'}`}
      </p>
    </div>
  );
}

/** Department card with its members listed underneath, as one fixed-width block. */
function DeptBlock({ dept, headLookup, onEdit }: NodeCardProps) {
  return (
    <div className="flex w-60 flex-col">
      <NodeCard dept={dept} headLookup={headLookup} onEdit={onEdit} />
      {dept.agents.length > 0 && (
        <MemberList agents={dept.agents} headName={dept.head_agent_name} />
      )}
    </div>
  );
}

/**
 * One subtree: the department card, a vertical stem down to its children, and the
 * children laid out side by side with a horizontal connector bar between them.
 * Connectors are plain borders so nothing needs measuring or a layout library.
 */
function Subtree({
  node,
  headLookup,
  onEdit,
}: {
  node: DeptNode;
  headLookup: Map<string, AgentCardData>;
  onEdit?: (dept: DepartmentRow) => void;
}) {
  const hasChildren = node.children.length > 0;

  return (
    <div className="flex flex-col items-center">
      <DeptBlock dept={node} headLookup={headLookup} onEdit={onEdit} />

      {hasChildren && (
        <>
          {/* stem out of the parent */}
          <span aria-hidden className="h-5 w-px bg-border" />
          <div className="flex items-start">
            {node.children.map((child, i) => {
              const isFirst = i === 0;
              const isLast = i === node.children.length - 1;
              return (
                // The column itself carries no padding, so adjacent rails meet edge to edge
                // and read as one continuous bar; the gap between cards comes from the inner px-3.
                <div key={child.id} className="flex flex-col items-center">
                  {/* connector: half-bars stop at the outer children so the rail spans centre-to-centre */}
                  <div aria-hidden className="relative h-5 w-full">
                    {!isFirst && (
                      <span className="absolute left-0 right-1/2 top-0 h-px bg-border" />
                    )}
                    {!isLast && <span className="absolute left-1/2 right-0 top-0 h-px bg-border" />}
                    <span className="absolute left-1/2 top-0 h-5 w-px bg-border" />
                  </div>
                  <div className="px-3">
                    <Subtree node={child} headLookup={headLookup} onEdit={onEdit} />
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

interface OrgChartProps {
  departments: DepartmentWithAgents[];
  /** Every colleague — used to resolve department heads and the unassigned bucket. */
  agents: AgentCardData[];
  unassigned: AgentCardData[];
  onEdit?: (dept: DepartmentRow) => void;
}

export function OrgChart({ departments, agents, unassigned, onEdit }: OrgChartProps) {
  const roots = useMemo(() => buildTree(departments), [departments]);
  const headLookup = useMemo(() => new Map(agents.map((a) => [a.name, a])), [agents]);

  if (roots.length === 0 && unassigned.length === 0) {
    return (
      <div className="rounded-lg border border-dashed py-16 text-center text-muted-foreground">
        No departments configured yet.
      </div>
    );
  }

  return (
    <div className="overflow-x-auto rounded-lg border bg-muted/20 p-6 scrollbar-thin">
      <div className="flex min-w-max items-start justify-center gap-8">
        {roots.map((root) => (
          <Subtree key={root.id} node={root} headLookup={headLookup} onEdit={onEdit} />
        ))}
      </div>

      {unassigned.length > 0 && (
        <div className="mt-8 flex flex-col items-center border-t border-dashed pt-6">
          <DeptBlock
            headLookup={headLookup}
            dept={{
              id: -1,
              name: 'Unassigned',
              goal: 'Colleagues with no department, or a department that no longer exists.',
              responsibilities: null,
              head_agent_name: null,
              parent_dept_id: null,
              agents: unassigned,
            }}
          />
        </div>
      )}
    </div>
  );
}
